import { useCalculateOrderMutation } from "../queries/auto-generated/hooks";
import { useSelector } from "react-redux";
import { RootState } from "../ducks";
import _ from "lodash";
import { useEffect } from "react";
import { prettyMoney, builtItemsToLineItems } from "../helpers";
import useCurrentLocation from "./useCurrentLocation";
import hash from "object-hash";
import { TipData } from "../types";

/**
 * Asks Square to calculate the total cost of the cart.
 */
const useCalculateFinalCost = () => {
  const builtItems = useSelector((state: RootState) => state.cart.lineItems);
  const { tip } = useSelector((state: RootState) => state.checkout);
  const variations = useSelector(
    (state: RootState) => state.catalog.data.variations
  );
  const { current } = useCurrentLocation();

  const location_id = current?.square_id ?? "";

  const variationsLength = _.size(variations);

  const line_items =
    variationsLength > 0 ? builtItemsToLineItems(builtItems, variations) : [];

  const orderRequest =
    variationsLength > 0
      ? {
          location_id,
          line_items,
        }
      : null;

  const [
    calculateOrderMutation,
    { loading, error, data },
  ] = useCalculateOrderMutation();

  const line_items_hash = hash(line_items); // To help prevent unnecessary callsÎ

  useEffect(() => {
    if (orderRequest) {
      calculateOrderMutation({
        variables: {
          input: orderRequest,
        },
      })
        .then((response) => {
          if (response.errors) {
            console.error(response.errors);
          }
        })
        .catch((error) => console.error(error));
    }
    // eslint-disable-next-line
  }, [calculateOrderMutation, line_items_hash, location_id]);

  // Taxes
  const taxes = data?.calculateOrder?.order?.total_tax_money?.amount ?? null;
  const prettyTaxes = taxes ? prettyMoney(taxes) : null;

  const totalBeforeTip =
    data?.calculateOrder?.order?.total_money?.amount ?? null;

  // Subtotal
  const subtotal = taxes && totalBeforeTip ? totalBeforeTip - taxes : null;
  const prettySubtotal = subtotal ? prettyMoney(subtotal) : null;

  // Optional tip
  const tipAmount = tip && subtotal ? calculateTipAmount(tip, subtotal) : null;
  const prettyTipAmount = tipAmount ? prettyMoney(tipAmount) : null;

  // Calculate total
  const total = totalBeforeTip ? totalBeforeTip + (tipAmount || 0) : null;
  const prettyTotal = total ? prettyMoney(total) : null;

  return {
    orderRequest,
    loading,
    error,
    data,
    subtotal,
    taxes,
    total, // TODO: TEST
    tipAmount,
    prettySubtotal,
    prettyTaxes,
    prettyTotal,
    prettyTipAmount,
  };
};

/**
 * Calculate the tip amount in cents given the tip information and the subtotal
 * @param tip 
 * @param subtotal 
 * @return tip amount in cents
 */
const calculateTipAmount = (tip: TipData, subtotal: number): number => {
  switch (tip.type) {
    case "none":
      return 0;
    case "cents":
      return tip?.value ?? 0;
    case "percentage":
      return Math.round(((tip?.value ?? 0) * subtotal) / 100);
    default:
      return 0;
  }
};

export default useCalculateFinalCost;

export type FinalCostReturnValue = ReturnType<typeof useCalculateFinalCost>;
